import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils.js";

/**
 * How the glow is driven:
 * - "pointer": lights the stretch of border nearest the cursor (default)
 * - "animated": one automatic sweep around the border on mount / sweepKey change
 * - "both": the sweep, then pointer tracking (moving the pointer cancels a sweep)
 * - "none": no glow at all -- a plain card with the same base styling
 */
export type GlowCardInteraction = "pointer" | "animated" | "both" | "none";

/**
 * Card surface + the static half of the glow. Every variant only sets a CSS
 * custom property that the layer styles in tokens.css (.glow-card) read, so
 * each can be swapped independently; for a one-off palette, pass custom
 * properties via className, e.g. className="[--gc-c1:#8b5cf6] [--gc-c2:#ec4899]".
 */
const glowCardVariants = cva(
  "relative isolate rounded-[var(--gc-radius)] border border-surface-200 bg-surface-0 text-left",
  {
    variants: {
      /** Mesh colors along the border (--gc-c1..3) and the dark-mode hot spot (--gc-hot). */
      tone: {
        aurora: "[--gc-c1:var(--aurora-1)] [--gc-c2:var(--aurora-2)] [--gc-c3:var(--aurora-3)] [--gc-hot:hsl(40deg_80%_80%)]",
        brand: "[--gc-c1:var(--color-brand-500)] [--gc-c2:var(--color-info-accent)] [--gc-c3:#7dd3fc] [--gc-hot:#dbeafe]",
        success: "[--gc-c1:var(--color-success-accent)] [--gc-c2:#2dd4bf] [--gc-c3:#a3e635] [--gc-hot:#dcfce7]",
        warning: "[--gc-c1:var(--color-warning-accent)] [--gc-c2:#fb923c] [--gc-c3:#facc15] [--gc-hot:#fef3c7]",
        danger: "[--gc-c1:var(--color-error-accent)] [--gc-c2:#f472b6] [--gc-c3:#fb923c] [--gc-hot:#fee2e2]",
      },
      /** Border glow strength: bloom brightness, how far it reaches past the border, and the edge tint. */
      glow: {
        off: "[--gc-border:0]",
        // The lightest touch above "off": the edge nearest the cursor lifts just
        // enough to show the card reacts, with no bloom past the border.
        ultra: "[--gc-intensity:0.06] [--gc-pad:6px] [--gc-fill:0.03]",
        // Barely-there edge light for large, text-heavy cards (profile sections),
        // where "low" still reads as a highlight around the whole card.
        faint: "[--gc-intensity:0.3] [--gc-pad:14px] [--gc-fill:0.15]",
        low: "[--gc-intensity:0.6] [--gc-pad:24px] [--gc-fill:0.3]",
        medium: "[--gc-intensity:1] [--gc-pad:40px] [--gc-fill:0.5]",
        high: "[--gc-intensity:1.5] [--gc-pad:56px] [--gc-fill:0.7]",
      },
      /** Interior light that follows the cursor while hovered. */
      spotlight: {
        off: "",
        low: "[--gc-spot-strength:0.6]",
        medium: "[--gc-spot-strength:1]",
        high: "[--gc-spot-strength:1.6]",
      },
      /** Radius of the interior light pool. */
      spotlightSize: {
        sm: "[--gc-spot-size:160px]",
        md: "[--gc-spot-size:260px]",
        lg: "[--gc-spot-size:400px]",
      },
      /** How close to an edge the pointer must get before the glow appears. */
      reach: {
        tight: "[--gc-sensitivity:50]",
        normal: "[--gc-sensitivity:30]",
        wide: "[--gc-sensitivity:10]",
      },
      /** How much of the perimeter the lit stretch covers. */
      spread: {
        narrow: "[--gc-cone:15]",
        normal: "[--gc-cone:25]",
        wide: "[--gc-cone:35]",
      },
      radius: {
        md: "[--gc-radius:var(--radius-md)]",
        lg: "[--gc-radius:var(--radius-lg)]",
        xl: "[--gc-radius:var(--radius-xl)]",
        "2xl": "[--gc-radius:28px]",
      },
      /** Card size (inner spacing). "none" leaves layout entirely to the content. */
      size: {
        none: "",
        sm: "p-3",
        md: "p-4",
        lg: "p-6",
      },
      selected: {
        true: "border-[color:var(--color-accent-border)] bg-[var(--color-accent-bg-subtle)]",
        false: "",
      },
      /** Set automatically when the card renders as a <button>. */
      interactive: {
        true: "cursor-pointer transition-colors hover:border-surface-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
        false: "",
      },
    },
    defaultVariants: {
      tone: "aurora",
      glow: "medium",
      spotlight: "medium",
      spotlightSize: "md",
      reach: "normal",
      spread: "normal",
      radius: "lg",
      size: "md",
      selected: false,
      interactive: false,
    },
  }
);

/**
 * Every default GlowCard falls back to -- a bare <GlowCard>…</GlowCard> works
 * with just these. Pass any of them at the call site to change it, including
 * a value computed at runtime, e.g. glow={needsReview ? "high" : "low"}.
 */
export const glowCardDefaults = {
  as: "div",
  interaction: "pointer",
  size: "md",
  glow: "medium",
  spotlight: "medium",
  spotlightSize: "md",
  tone: "aurora",
  reach: "normal",
  spread: "normal",
  radius: "lg",
} as const;

export interface GlowCardProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "color">,
    Omit<VariantProps<typeof glowCardVariants>, "interactive"> {
  /** Rendered element. "button" makes the whole card the click target (with a focus ring). */
  as?: "div" | "button" | "article" | "section" | "li";
  interaction?: GlowCardInteraction;
  /** "animated" / "both": changing this value replays the sweep (e.g. when new data arrives). */
  sweepKey?: React.Key;
  /** Only used when as="button". */
  type?: "button" | "submit" | "reset";
  /** Only used when as="button". */
  disabled?: boolean;
  /**
   * Exact card dimensions. A number is px (436 → 436px); a string is any CSS
   * length ("100%", "20rem", "calc(100% - 32px)"). Omit to size from content
   * and the surrounding layout, as before.
   */
  width?: CardDimension;
  height?: CardDimension;
  minWidth?: CardDimension;
  minHeight?: CardDimension;
  maxWidth?: CardDimension;
  maxHeight?: CardDimension;
}

type CardDimension = number | string;

const easeOutCubic = (x: number) => 1 - Math.pow(1 - x, 3);
const easeInCubic = (x: number) => x * x * x;

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Sweep timeline (ms): the glow rises, travels clockwise ~1 turn, then fades.
const SWEEP = { riseMs: 500, fadeFromMs: 2500, endMs: 4000, turnMs: 1500, settleMs: 3750, a0: 110, a1: 355, a2: 465 };

function sweepFrame(t: number): { edge: number; angle: number } {
  const edge =
    t < SWEEP.riseMs ? easeOutCubic(t / SWEEP.riseMs)
    : t < SWEEP.fadeFromMs ? 1
    : 1 - easeInCubic(Math.min(1, (t - SWEEP.fadeFromMs) / (SWEEP.endMs - SWEEP.fadeFromMs)));
  const angle =
    t < SWEEP.turnMs ? SWEEP.a0 + (SWEEP.a1 - SWEEP.a0) * easeInCubic(t / SWEEP.turnMs)
    : SWEEP.a1 + (SWEEP.a2 - SWEEP.a1) * easeOutCubic(Math.min(1, (t - SWEEP.turnMs) / (SWEEP.settleMs - SWEEP.turnMs)));
  return { edge, angle };
}

const GlowCard = React.forwardRef<HTMLElement, GlowCardProps>(
  (
    {
      as = glowCardDefaults.as,
      interaction = glowCardDefaults.interaction,
      tone, glow, spotlight, spotlightSize, reach, spread, radius, size, selected,
      width, height, minWidth, minHeight, maxWidth, maxHeight,
      sweepKey, type, className, style, children, onPointerMove, ...props
    },
    ref
  ) => {
    const nodeRef = React.useRef<HTMLElement | null>(null);
    const pointerFrame = React.useRef(0);
    const sweepFrameId = React.useRef(0);
    const lastPointer = React.useRef({ x: 0, y: 0 });

    const glowOn = interaction !== "none";
    const tracksPointer = interaction === "pointer" || interaction === "both";
    const sweeps = interaction === "animated" || interaction === "both";
    // The interior light only exists under a hovering pointer.
    const spotOn = tracksPointer && (spotlight ?? glowCardDefaults.spotlight) !== "off";

    // Writes the live glow position straight onto the DOM node -- it follows
    // the pointer (or a sweep frame), so it's recomputed on every event and
    // can't be a static class (CLAUDE.md "JS-driven continuous animation
    // values" exception). No React state, so moving the pointer never re-renders.
    const setGlow = (edge: number, angle: number) => {
      const node = nodeRef.current;
      if (!node) return;
      node.style.setProperty("--gc-edge", (edge * 100).toFixed(2));
      node.style.setProperty("--gc-angle", `${angle.toFixed(2)}deg`);
    };

    const stopSweep = () => {
      cancelAnimationFrame(sweepFrameId.current);
      sweepFrameId.current = 0;
      delete nodeRef.current?.dataset.glowSweeping;
    };

    React.useEffect(() => {
      if (!sweeps || prefersReducedMotion()) return;
      const node = nodeRef.current;
      if (!node) return;
      node.dataset.glowSweeping = "";
      const start = performance.now();
      const step = (now: number) => {
        const t = now - start;
        const { edge, angle } = sweepFrame(t);
        setGlow(edge, angle);
        if (t < SWEEP.endMs) sweepFrameId.current = requestAnimationFrame(step);
        else stopSweep();
      };
      sweepFrameId.current = requestAnimationFrame(step);
      return stopSweep;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sweeps, sweepKey]);

    React.useEffect(() => () => cancelAnimationFrame(pointerFrame.current), []);

    const handlePointerMove = (e: React.PointerEvent<HTMLElement>) => {
      onPointerMove?.(e);
      if (!tracksPointer || e.pointerType === "touch") return;
      if (sweepFrameId.current) stopSweep();
      lastPointer.current = { x: e.clientX, y: e.clientY };
      // At most one layout read + style write per frame, using the latest position.
      if (pointerFrame.current) return;
      pointerFrame.current = requestAnimationFrame(() => {
        pointerFrame.current = 0;
        const node = nodeRef.current;
        if (!node) return;
        const rect = node.getBoundingClientRect();
        const cx = rect.width / 2;
        const cy = rect.height / 2;
        if (!cx || !cy) return;
        const x = lastPointer.current.x - rect.left;
        const y = lastPointer.current.y - rect.top;
        const dx = x - cx;
        const dy = y - cy;
        // 0 at the center, 1 on the border: how far along the ray from the
        // center toward the pointer it sits, relative to where that ray exits
        // the rectangle (whichever of the x / y half-extents it hits first).
        const edge = Math.min(1, Math.max(Math.abs(dx) / cx, Math.abs(dy) / cy));
        // atan2 measures from +x (right); conic-gradient's 0deg points up.
        let angle = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
        if (angle < 0) angle += 360;
        setGlow(edge, angle);
        // Interior spotlight center -- same per-move pointer position.
        if (spotOn) {
          node.style.setProperty("--mouse-x", `${x.toFixed(1)}px`);
          node.style.setProperty("--mouse-y", `${y.toFixed(1)}px`);
        }
      });
    };

    const setRefs = (node: HTMLElement | null) => {
      nodeRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLElement | null>).current = node;
    };

    const Comp = as as React.ElementType;
    const isButton = as === "button";
    // Caller-chosen dimensions (CLAUDE.md "caller-supplied dimensions on ui/
    // primitives" exception) -- any number/length, possibly from runtime data,
    // which a literal Tailwind class can't express. Undefined keys are dropped.
    const sizeStyle: React.CSSProperties = { width, height, minWidth, minHeight, maxWidth, maxHeight };

    return (
      <Comp
        ref={setRefs}
        type={isButton ? (type ?? "button") : undefined}
        data-glow-pointer={tracksPointer ? "" : undefined}
        onPointerMove={glowOn ? handlePointerMove : onPointerMove}
        style={{ ...sizeStyle, ...style }}
        className={cn(
          glowCardVariants({ tone, glow, spotlight, spotlightSize, reach, spread, radius, size, selected, interactive: isButton }),
          glowOn && "glow-card",
          className
        )}
        {...props}
      >
        {glowOn && <span aria-hidden="true" className="glow-card-light" />}
        {spotOn && <span aria-hidden="true" className="glow-card-spot" />}
        {children}
      </Comp>
    );
  }
);
GlowCard.displayName = "GlowCard";

export { GlowCard, glowCardVariants };
